import React, { useCallback } from 'react';

import { styled } from '@compiled/react';
import { Link } from 'react-resource-router';

import SearchIcon from '@atlaskit/icon/core/search';
import { Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ReturnIcon, useKeyboardNavigation } from '@atlassian/search-dialog';

const StyledLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: `${token('space.100')} ${token('space.200')}`,
  backgroundColor: token('color.background.neutral.subtle'),
  color: token('color.text'),
  textDecoration: 'none',
  boxSizing: 'border-box',
  font: token('font.body'),
  // highlight styles toggled via className to simulate :hover state when
  // using keyboard navigation with arrow keys
  '&:hover, &.keyboard-highlight': {
    backgroundColor: token('color.background.neutral'),
    textDecoration: 'none',
    color: token('color.text'),
  },
});

export interface BitbucketSearchLinkProps {
  href: string;
  label: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const BitbucketSearchLink = ({
  href,
  label,
  onClick,
}: BitbucketSearchLinkProps) => {
  const [isKeyboardHighlighted, ref] = useKeyboardNavigation<HTMLAnchorElement>(
    {
      // Focus stays on the search input during keyboard/arrow key navigation (by design
      // in @atlassian/search-dialog), so Enter won't natively click the
      // highlighted link. We manually .click() the link element here.
      onKeydownCallback: useCallback(
        (e: KeyboardEvent, target: HTMLElement) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            target.click?.();
          }
        },
        []
      ),
    }
  );

  return (
    // Using react-resource-router Link here in order to forward the ref to the anchor element
    <StyledLink
      to={href}
      ref={ref}
      onClick={onClick}
      className={isKeyboardHighlighted ? 'keyboard-highlight' : undefined}
    >
      <Inline space="space.200" alignBlock="center">
        <SearchIcon color="currentColor" label="" />
        <span>{label}</span>
      </Inline>
      <ReturnIcon />
    </StyledLink>
  );
};
